<template>
  <div>
    <v-toolbar flat dense class="mb-2">
      <v-btn icon @click="prev" :disabled="loading"><v-icon>chevron_left</v-icon></v-btn>
      <v-btn icon @click="next" :disabled="loading"><v-icon>chevron_right</v-icon></v-btn>
      <v-btn text small @click="goToday" :disabled="loading" class="ml-1">{{ $t('leave_calendar.today') }}</v-btn>
      <v-toolbar-title v-if="data" class="subtitle-1 ml-2">
        {{ $formatDateNoTime(data.dateFrom) }} – {{ $formatDateNoTime(data.dateTo) }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" outlined small :loading="printingPdf" :disabled="loading" @click="printPdf" class="mr-2">
        <v-icon left small>mdi mdi-printer</v-icon>{{ $t('leave_calendar.print_pdf') }}
      </v-btn>
      <v-btn color="primary" outlined small :loading="printing" :disabled="loading" @click="print">
        <v-icon left small>mdi mdi-file-excel</v-icon>{{ $t('leave_calendar.print_excel') }}
      </v-btn>
    </v-toolbar>

    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

    <div v-if="data" class="calendar-scroll">
      <table class="leave-calendar">
        <colgroup>
          <col class="col-name" />
          <template v-for="(week, wi) in data.weeks">
            <col v-for="d in 7" :key="wi + '-' + d" />
          </template>
        </colgroup>
        <thead>
          <tr>
            <th rowspan="3" class="name-col">{{ $t('leave_calendar.name_column') }}</th>
            <th v-for="(month, mi) in data.months" :key="'m' + mi" :colspan="month.weekSpan * 7" class="month-cell">
              {{ month.name }}
            </th>
          </tr>
          <tr>
            <th v-for="(week, wi) in data.weeks" :key="'w' + wi" colspan="7" class="week-cell">
              {{ $t('leave_calendar.week_short') }} {{ week.weekNumber }}
            </th>
          </tr>
          <tr>
            <th v-for="(week, wi) in data.weeks" :key="'r' + wi" colspan="7" class="range-cell">{{ week.rangeLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="worker in data.workers" :key="worker.id">
            <td class="name-col">{{ worker.name }}</td>
            <td
              v-for="(seg, si) in worker.segments"
              :key="si"
              :colspan="seg.length"
              :class="seg.covered ? 'bar-cell' : 'gap-cell'"
            >
              <v-tooltip v-if="seg.covered" top>
                <template v-slot:activator="{ on, attrs }">
                  <div class="bar" v-bind="attrs" v-on="on">{{ seg.label }}</div>
                </template>
                <div>{{ seg.typeName }}</div>
                <div>{{ segDateLabel(seg) }}</div>
              </v-tooltip>
            </td>
          </tr>
          <tr v-if="data.workers.length === 0">
            <td :colspan="data.weeks.length * 7 + 1" class="text-center grey--text pa-4">
              {{ $t('leave_calendar.no_workers') }}
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="data.legend.length > 0" class="legend mt-3">
        <strong>{{ $t('leave_calendar.legend') }}:</strong>
        <span v-for="item in data.legend" :key="item.code" class="legend-item">
          <span class="legend-code">{{ item.code }}</span> = {{ item.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import downloadjs from 'downloadjs';
import reportApi from '@/api/report';

const WEEKS_TO_SHOW = 20;

export default {
  name: 'LeaveCalendar',
  data() {
    return {
      loading: false,
      printing: false,
      printingPdf: false,
      data: null,
      startMonday: dayjs().startOf('month').startOf('isoWeek'),
    };
  },
  computed: {
    startDate() {
      return this.startMonday.format('YYYY-MM-DD');
    },
    endDate() {
      return this.startMonday
        .add(WEEKS_TO_SHOW - 1, 'week')
        .add(6, 'day')
        .format('YYYY-MM-DD');
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.data = await reportApi.getLeaveCalendarData(this.startDate, this.endDate);
      } catch (e) {
        console.error(e);
        this.$showErrorNotification(this.$t('leave_calendar.load_error'));
      }
      this.loading = false;
    },
    prev() {
      this.startMonday = this.startMonday.subtract(4, 'week');
      this.load();
    },
    next() {
      this.startMonday = this.startMonday.add(4, 'week');
      this.load();
    },
    goToday() {
      this.startMonday = dayjs().startOf('month').startOf('isoWeek');
      this.load();
    },
    segDateLabel(seg) {
      const start = this.$formatDateNoTime(seg.startDate);
      const end = this.$formatDateNoTime(seg.endDate);
      return start === end ? start : `${start}–${end}`;
    },
    async print() {
      this.printing = true;
      try {
        const blob = await reportApi.getLeaveCalendarXlsx(this.startDate, this.endDate);
        const filename = this.$fileNameSanitizer(
          `lomakalenteri-${this.startDate.replace(/-/g, '')}-${this.endDate.replace(/-/g, '')}.xlsx`
        );
        downloadjs(blob, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      } catch (e) {
        console.error(e);
        this.$showErrorNotification(this.$t('leave_calendar.print_error'));
      }
      this.printing = false;
    },
    async printPdf() {
      this.printingPdf = true;
      try {
        const blob = await reportApi.getLeaveCalendarReport(this.startDate, this.endDate);
        const filename = this.$fileNameSanitizer(
          `lomakalenteri-${this.startDate.replace(/-/g, '')}-${this.endDate.replace(/-/g, '')}.pdf`
        );
        downloadjs(blob, filename, 'application/pdf');
      } catch (e) {
        console.error(e);
        this.$showErrorNotification(this.$t('leave_calendar.print_error'));
      }
      this.printingPdf = false;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.calendar-scroll {
  overflow-x: auto;
}
.leave-calendar {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  white-space: nowrap;
  font-size: 12px;
}
.leave-calendar .col-name {
  width: 160px;
}
.leave-calendar th {
  border: 1px solid #ccc;
  padding: 2px 6px;
  text-align: center;
  background-color: #f0f0f0;
  font-weight: bold;
}
.leave-calendar td {
  padding: 0;
  height: 22px;
  border-bottom: 1px solid #eee;
}
.leave-calendar .month-cell {
  text-align: left;
}
.leave-calendar .name-col {
  text-align: left;
  padding: 2px 6px;
  border-right: 1px solid #ccc;
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 1;
}
.leave-calendar thead .name-col {
  background-color: #f0f0f0;
}
/* gap (working days) cells carry the week grid lines */
.leave-calendar td.gap-cell {
  border-right: 1px solid #eee;
}
/* one continuous absence run = a single pill with its label centered */
.leave-calendar td.bar-cell {
  padding: 0 1px;
}
.leave-calendar .bar {
  height: 18px;
  border-radius: 9px;
  background-color: #90caf9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  overflow: hidden;
}
.legend {
  font-size: 12px;
}
.legend-item {
  margin-left: 12px;
  white-space: nowrap;
}
.legend-code {
  display: inline-block;
  background-color: #90caf9;
  border-radius: 4px;
  padding: 0 6px;
  font-weight: bold;
}
</style>
